import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { SiteRagReport } from '../../../types/siteRag';
import { buildReportSummaryModel, loadFrozenReportSummary, reportSummaryEligibility, type ReportSummaryModelV1 } from '../../../lib/reportSummary';
import { authedFetch } from '../../../lib/authedFetch';
import { reportIntentIsCurrent } from '../../../lib/pathToGo/reportPlan';
import { siteOverviewPath } from '../../../lib/siteNavigation';
import { useStore } from '../../../store';
import { ReportSummaryDocument } from './ReportSummaryDocument';
import { ReportSummaryToolbar } from './ReportSummaryToolbar';
import { downloadReportSummary, prebuildReportSummary } from './reportSummaryArtifactActions';

export function ReportSummaryView({ report }: { report: SiteRagReport }) {
  const [params, setParams] = useSearchParams();
  const [saved, setSaved] = useState<{ report: SiteRagReport; model: ReportSummaryModelV1; pageSize: 'A4' | 'Letter' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const hydrate = useStore(state => state.hydrateReportBody);
  const companyId = useStore(state => state.companyId);
  const liveSite = useStore(s => (s.siteRag?.sites ?? (s as unknown as { sites?: Array<{ id: string; projectIntent?: SiteRagReport['projectIntentAtGen'] }> }).sites ?? []).find(site => site.id === report.siteId));
  const summaryIdentity = [
    companyId ?? '',
    report.siteId ?? '',
    report.id,
    report.reportRevision?.revisionId ?? '',
    report.reportRevision?.documentHash ?? '',
  ].join('::');
  const summaryIdentityRef = React.useRef(summaryIdentity);
  const downloadRequestSeqRef = React.useRef(0);
  const lifecycleEpochRef = React.useRef(0);
  const isMountedRef = React.useRef(true);
  if (summaryIdentityRef.current !== summaryIdentity) {
    summaryIdentityRef.current = summaryIdentity;
    lifecycleEpochRef.current += 1;
    downloadRequestSeqRef.current += 1;
  }
  const isDownloadRequestCurrent = (
    requestIdentity: string,
    requestSeq: number,
    requestEpoch: number,
  ): boolean =>
    isMountedRef.current
    && lifecycleEpochRef.current === requestEpoch
    && summaryIdentityRef.current === requestIdentity
    && downloadRequestSeqRef.current === requestSeq;
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      lifecycleEpochRef.current += 1;
      downloadRequestSeqRef.current += 1;
    };
  }, []);
  const size = params.get('size') === 'letter' ? 'Letter' : params.get('size') === 'a4' ? 'A4' : saved?.pageSize ?? 'A4';
  useEffect(() => {
    let cancelled = false;
    const expectedCompanyId = companyId ?? null;
    setSaved(null); setError(null); setPending(false);
    void (async () => {
      let source = report;
      if (source._metadataOnly) {
        if (!await hydrate(source.id)) throw new Error('The complete saved report could not be loaded.');
        if (cancelled || (useStore.getState().companyId ?? null) !== expectedCompanyId) return;
        const body = useStore.getState().siteRag.reports.find(candidate => candidate.id === report.id);
        if (!body || body._metadataOnly || body.reportRevision?.revisionId !== report.reportRevision?.revisionId) throw new Error('The saved revision changed while loading.');
        source = body;
      }
      const frozen = await loadFrozenReportSummary(source, async path => {
        const response = await authedFetch(`/api/storage/load?path=${encodeURIComponent(path)}`);
        if (!response.ok) throw new Error('The saved canonical report could not be loaded.');
        return response.json();
      });
      if (cancelled || (useStore.getState().companyId ?? null) !== expectedCompanyId) return;
      const eligibility = reportSummaryEligibility(frozen.report);
      if (!eligibility.ok) throw new Error(eligibility.reason);
      const model = buildReportSummaryModel(frozen.report, frozen.document);
      if (!cancelled && (useStore.getState().companyId ?? null) === expectedCompanyId) {
        setSaved({ report: frozen.report, model, pageSize: frozen.presentation.pageSize });
        const storeState = useStore.getState();
        const siteInStore = (storeState.siteRag?.sites ?? (storeState as unknown as { sites?: Array<{ id: string; projectIntent?: SiteRagReport['projectIntentAtGen'] }> }).sites ?? []).find(site => site.id === frozen.report.siteId);
        const isCurrentIntent = !siteInStore?.projectIntent || reportIntentIsCurrent(frozen.report, siteInStore.projectIntent);
        if (isCurrentIntent) {
          const targetPaper = params.get('size') === 'letter' ? 'Letter' : params.get('size') === 'a4' ? 'A4' : frozen.presentation.pageSize;
          if (expectedCompanyId) prebuildReportSummary(frozen.report, targetPaper, expectedCompanyId);
          else prebuildReportSummary(frozen.report, targetPaper);
        }
      }
    })().catch(reason => {
      if (!cancelled && (useStore.getState().companyId ?? null) === expectedCompanyId) {
        setError((reason as Error).message);
      }
    });
    return () => { cancelled = true; };
  }, [report, hydrate, companyId]);
  const intentDriftNotice = useMemo(() => {
    if (!saved?.report || !liveSite?.projectIntent) return null;
    if (reportIntentIsCurrent(saved.report, liveSite.projectIntent)) return null;
    return 'Intent drift: Live Project Intent has changed since this frozen revision was generated. Rerun the report to generate a new revision and PDF for the current Project Intent.';
  }, [saved?.report, liveSite?.projectIntent]);
  if (error) return <div className="p-8 space-y-4"><h1 className="text-xl font-semibold">Saved summary unavailable</h1><p>{error}</p><Link className="inline-flex min-h-11 items-center underline" to={siteOverviewPath(report.siteId ?? '', 'reports')}>Back to Analyses</Link></div>;
  if (!saved) return <div className="p-8" role="status">Loading the saved three-page summary…</div>;
  return <ReportSummaryDocument model={saved.model} pageSize={size}>
    {intentDriftNotice && (
      <div role="status" data-testid="report-summary-intent-drift-notice" className="mx-auto mb-3 max-w-5xl rounded border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-200">
        {intentDriftNotice}
      </div>
    )}
    <ReportSummaryToolbar model={saved.model} pageSize={size} pending={pending}
      onPageSize={paper => {
        setParams({ size: paper.toLowerCase() }, { replace: true });
        if (!intentDriftNotice) {
          if (companyId) prebuildReportSummary(saved.report, paper, companyId);
          else prebuildReportSummary(saved.report, paper);
        }
      }}
      onDownload={() => {
        const requestIdentity = summaryIdentity;
        const requestEpoch = lifecycleEpochRef.current;
        const requestSeq = ++downloadRequestSeqRef.current;
        setPending(true);
        void downloadReportSummary(saved.report, {
          pageSize: size,
          companyId: companyId ?? null,
          shouldSave: () => isDownloadRequestCurrent(requestIdentity, requestSeq, requestEpoch),
        }).catch(reason => {
          if (isDownloadRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
            toast.error((reason as Error).message);
          }
        }).finally(() => {
          if (isDownloadRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
            setPending(false);
          }
        });
      }} />
  </ReportSummaryDocument>;
}
